import {
  CALCULATOR_HANDOFF_PARAM,
  decodeCalculatorHandoff,
  type CalculatorHandoff,
} from '@etap/shared';

const HANDOFF_KEY = 'etap_calculator_handoff';
const RETURN_TO_KEY = 'etap_post_auth_return_to';

/** Результат калькулятора протухает через сутки — чтобы старая вкладка не
 *  подставила вчерашние цифры в новый проект. */
const TTL_MS = 24 * 60 * 60 * 1000;

interface StoredHandoff {
  data: CalculatorHandoff;
  storedAt: number;
}

/**
 * localStorage, а не sessionStorage: вход через Yandex ID уводит пользователя
 * на сторонний домен и возвращает обратно, а Telegram-виджет может открыться
 * в новой вкладке — sessionStorage такого не переживает.
 */
function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Приватный режим / переполнение — переносить нечего, не падаем.
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // no-op
  }
}

/**
 * Читает результат калькулятора из URL и запоминает его.
 * Вызывать при старте приложения — до авторизации, потому что до неё
 * пользователь может уехать на OAuth-редирект и вернуться уже без query-строки.
 */
export function storeCalculatorHandoff(): void {
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const raw = params.get(CALCULATOR_HANDOFF_PARAM);

  const decoded = decodeCalculatorHandoff(raw) ?? legacyFromQuery(params);
  if (!decoded) return;

  const payload: StoredHandoff = { data: decoded, storedAt: Date.now() };
  safeSet(HANDOFF_KEY, JSON.stringify(payload));

  // Куда вернуть человека после OAuth-круга (Yandex уводит на свой домен).
  const destination = `${window.location.pathname}${window.location.search}`;
  if (window.location.pathname !== '/') {
    safeSet(RETURN_TO_KEY, destination);
  }
}

/**
 * Старый формат ссылки (?budget=&area=&city=) без разбивки по категориям —
 * ссылки из уже открытых вкладок и из истории должны продолжать работать.
 */
function legacyFromQuery(params: URLSearchParams): CalculatorHandoff | null {
  const budget = Number(params.get('budget'));
  const area = Number(params.get('area'));
  const city = params.get('city');

  const hasBudget = Number.isFinite(budget) && budget > 0;
  const hasArea = Number.isFinite(area) && area > 0;
  if (!hasBudget && !hasArea) return null;

  return {
    v: 1,
    city: city || null,
    area: hasArea ? area : 0,
    budget: hasBudget ? budget : 0,
    lines: [],
  };
}

/** Возвращает сохранённый результат, не удаляя его (для префилла формы). */
export function peekCalculatorHandoff(): CalculatorHandoff | null {
  const raw = safeGet(HANDOFF_KEY);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as StoredHandoff;
    if (!parsed?.storedAt || Date.now() - parsed.storedAt > TTL_MS) {
      safeRemove(HANDOFF_KEY);
      return null;
    }
    return parsed.data ?? null;
  } catch {
    safeRemove(HANDOFF_KEY);
    return null;
  }
}

/** Удаляет сохранённый результат — вызывать после успешного создания проекта. */
export function clearCalculatorHandoff(): void {
  safeRemove(HANDOFF_KEY);
  safeRemove(RETURN_TO_KEY);
}

/** Куда вернуть пользователя после OAuth-редиректа; одноразово. */
export function consumeReturnTo(): string | null {
  const value = safeGet(RETURN_TO_KEY);
  if (value) safeRemove(RETURN_TO_KEY);
  return value;
}
